import React, { ChangeEvent, useEffect, useState, useMemo } from "react";
import { Box, Button, Dialog, DialogContent, DialogTitle, InputAdornment, TextField, useMediaQuery } from "@mui/material";
import colors from "../../../shared/themes/colors";
import { useTheme } from "@mui/material/styles";
import Loader from "../../../shared/components/Loader";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import { CustomerMachineryState, Machine } from "../../machinery/models/models";
import { getMachines } from "../../machinery/customerMachinerySlice";
import MachineCard from "./MachineCard";
import MagnifierIcon from "../../../shared/assets/icons/MagnifierIcon";
import { dt } from "../../../shared/themes/redesignTokens";

interface MachineChoiceDialogProps {
  open: boolean;
  onClose: () => void;
  selectedMachineId?: string;
  onChoose: (machine: Machine) => void;
}
const MachineChoiceDialog = ({ open, onClose, selectedMachineId, onChoose }: MachineChoiceDialogProps) => {
  const dispatch = useAppDispatch();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { translate } = useTranslation(["myMachinery", "common"]);

  const { machines, loading } =
    useAppSelector<CustomerMachineryState>((state) => state.customerMachinery);

  const [searchQuery, setSearchQuery] = useState("");

  const allMachines = useMemo(() => Object.values(machines).flat(), [machines]);

  const filteredMachines = useMemo(() => {
    if (!searchQuery.trim()) {
      return allMachines;
    }

    const query = searchQuery.toLowerCase().trim();
    return allMachines.filter((machine) => {
      const modelMatch = machine.model?.toLowerCase().includes(query);
      const manufacturerMatch = machine.manufacturer?.name?.toLowerCase().includes(query);
      const customBrandMatch = machine.customBrandName?.toLowerCase().includes(query);

      return modelMatch || manufacturerMatch || customBrandMatch;
    });
  }, [allMachines, searchQuery]);

  const machinesCount = filteredMachines.length;
  const machinesText = machinesCount === 1 ?
    `${machinesCount} ${translate("myMachinery:machine")}`
    : `${machinesCount} ${translate("myMachinery:machines")}`;
  const noMachinesText = translate("myMachinery:noMachinesFound");

  const handleCancel = () => {
    onClose();
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  useEffect(() => {
    if (open) {
      dispatch(getMachines({}));
      setSearchQuery("");
    }
  }, [open, dispatch]);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      fullScreen={isMobile}
      maxWidth={false}
      PaperProps={{
        sx: {
          ...(isMobile ? {
            margin: 0,
            borderRadius: "8px",
            width: "100vw",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            padding: "16px",
            backgroundColor: colors.secondary.gray100,
          } : {
            width: "100%",
            maxWidth: "968px",
            borderRadius: "8px",
            padding: "24px 48px",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            backgroundColor: colors.secondary.gray100,
          })
        }
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          fontFamily: "Work Sans",
          fontSize: "24px",
          fontWeight: "500",
          lineHeight: "36px",
          color: colors.secondary.gray20,
          padding: "0 48px 24px !important",
          flex: "none",
          ...(isMobile && {
            paddingTop: "60px !important"
          })
        }}
      >
        {translate("myMachinery:chooseMachineForServiceRequest")}
      </DialogTitle>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          padding: "0 48px 24px",
        }}
      >
        <TextField
          placeholder={translate("myMachinery:machineNameOrBrand")}
          variant="outlined"
          color="primary"
          fullWidth
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MagnifierIcon color={dt.blue} />
              </InputAdornment>
            )
          }}
          sx={{
            marginBottom: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: "4px",
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: `${dt.blue} !important`,
              }
            }
          }}
        />
        {machinesCount > 0 ? (
          <Box className="text-20-medium" color={colors.secondary.gray20}>
            {machinesText.toLowerCase()}
          </Box>
        ) : (
          <Box className="text-20-medium" color={colors.secondary.gray20}>
            {noMachinesText}
          </Box>
        )}

        {loading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Loader />
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 2,
              overflow: "auto",
              my: 2,
            }}
          >
            {filteredMachines.map((machine) => (
              <MachineCard
                key={machine.id}
                machine={machine}
                onChoose={() => onChoose(machine)}
                showButton={true}
                isCurrentChoice={selectedMachineId === machine.id}
              />
            ))}
          </Box>
        )}
        <Button
          variant="outlined"
          onClick={handleCancel}
          sx={{
            width: "fit-content",
            minWidth: "102px",
            alignSelf: "center",
          }}
        >
          {translate("common:buttons.cancel")}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default MachineChoiceDialog;